import { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BookingSummaryCard } from "@/components/BookingSummaryCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  usePageMeta("Payment Confirmation", "USDD Academy payment confirmation");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", status: "open" });
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(false);
  const done = ["paid", "expired", "failed"].includes(status.payment_status);
  const exhausted = attempts >= 8 && !done;

  useEffect(() => {
    if (!sessionId || done || attempts >= 8) return;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/checkout/status/${sessionId}`);
        setStatus(r.data);
        if (r.data.payment_status === "paid") {
          trackEvent("purchase", {
            value: r.data.amount,
            currency: (r.data.currency || "gbp").toUpperCase(),
            session_id: sessionId,
            transaction_id: sessionId,
            method: "stripe",
            content_ids: r.data.sku ? [r.data.sku] : undefined,
            content_name: r.data.product_title || r.data.sku,
            content_type: "product",
          });
        }
      } catch (err) {
        if (err?.response?.status === 404) setError(true);
      }
      setAttempts((a) => a + 1);
    }, attempts === 0 ? 800 : 2000);
    return () => clearTimeout(t);
  }, [sessionId, attempts, done]);

  return (
    <>
      <Header />
      <main className="min-h-screen flex items-center justify-center bg-black pt-24 pb-16">
        <div className="max-w-xl mx-auto px-6 text-center">
          {!sessionId ? (
            <>
              <AlertCircle size={42} className="text-brand-purpleLight mx-auto mb-4" />
              <h1 className="font-display text-4xl text-white tracking-wide">No session found</h1>
              <p className="text-zinc-400 mt-3">Head back and try again.</p>
              <Link to="/pricing" className="mt-6 inline-block px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider">Back to pricing</Link>
            </>
          ) : error ? (
            <>
              <AlertCircle size={42} className="text-zinc-400 mx-auto mb-4" />
              <h1 className="font-display text-4xl text-white">Session not found</h1>
              <p className="text-zinc-400 mt-3 text-sm">We couldn't locate that checkout session.</p>
              <Link to="/pricing" className="mt-6 inline-block px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider">Back to pricing</Link>
            </>
          ) : status.payment_status === "paid" ? (
            <>
              <CheckCircle2 size={56} className="text-brand-yellow mx-auto mb-5" />
              <div className="text-[10px] uppercase tracking-[0.35em] text-brand-yellow mb-2">Booking Confirmed</div>
              <h1 className="font-display text-5xl text-white tracking-wide">You're in.</h1>
              <p className="text-zinc-300 mt-4">
                Your payment of <b className="text-brand-yellow">£{Number(status.amount || 0).toFixed(2)}</b> was successful.
                A confirmation email is on its way.
              </p>
              <BookingSummaryCard status={status} />
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/" className="px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover transition-all">Back to home</Link>
                <Link to="/events" className="px-6 py-3 rounded-full border border-white/20 text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black transition-all">See upcoming events</Link>
              </div>
            </>
          ) : status.status === "expired" || status.payment_status === "failed" || exhausted ? (
            <>
              <AlertCircle size={42} className="text-red-400 mx-auto mb-4" />
              <h1 className="font-display text-4xl text-white">{exhausted ? "Still pending…" : "Payment didn't go through"}</h1>
              <p className="text-zinc-400 mt-3">
                {exhausted
                  ? "We couldn't confirm your payment yet. If you were charged, you'll receive a confirmation email shortly."
                  : "No charge was made. You can try again."}
              </p>
              <div className="mt-6 flex gap-3 justify-center">
                <Link to="/pricing" className="px-6 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider">{exhausted ? "Back to pricing" : "Try again"}</Link>
                <Link to="/contact" className="px-6 py-3 rounded-full border border-white/20 text-white font-bold uppercase text-sm tracking-wider hover:bg-white hover:text-black transition-all">Contact us</Link>
              </div>
            </>
          ) : (
            <>
              <Loader2 size={42} className="text-brand-yellow mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-3xl text-white">Confirming your payment…</h1>
              <p className="text-zinc-400 mt-3 text-sm">This usually takes a few seconds.</p>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
